import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
// import { BiImport } from 'react-icons/bi';
import { FaPlus, FaEdit, FaTrash, FaEye } from 'react-icons/fa';
import { IoMdSearch } from 'react-icons/io';
import Select from 'react-select';
import TailwindDataTable from '../../../components/TailwindDataTable';
import PageSizeSelectBox from '../../../components/PageSizeSelectBox';
import ReactPaginate from 'react-paginate';
import { Link } from 'react-router-dom';
import { ApiPost } from '../../../helper/axiosFunctions';
import { commonToast } from '../../../helper/Toast';
import CreateClassmodel from '../../../components/model/CreateClassmodel';
import { parseListResponse } from '../../../helper/adminUsersApi';

export default function ClassList() {
    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [limit, setLimit] = useState(10);
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isAddOpen, setIsAddOpen] = useState(false);

    const columns = [
        {
            key: 'className',
            label: t('class_management.class_name'),
            formatter: (cell, row) => {
                return (
                    <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                <span className="text-gray-500 text-sm font-medium">
                                    {cell?.[0] || '?'}
                                </span>
                            </div>
                        </div>
                        <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{cell}</div>
                            <div className="text-sm text-gray-500">{row.description}</div>
                        </div>
                    </div>
                );
            }
        },
        {
            key: 'grade',
            label: t('class_management.class_grade'),
            formatter: (cell) => cell ? `${cell}${t('grade')}` : '-'
        },
        {
            key: 'subject',
            label: t('class_management.class_subject'),
            formatter: (cell) => cell || '-'
        },
        // {
        //     key: 'decks',
        //     label: t('deck'),
        //     formatter: (cell) => {
        //         if (!cell) return null;
        //         return (
        //             <div className="flex flex-wrap gap-1">
        //                 {cell.map((deck, index) => (
        //                     <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
        //                         {deck.name} ({deck.cardCount})
        //                     </span>
        //                 ))}
        //             </div>
        //         );
        //     }
        // },
        {
            key: 'students',
            label: t('student'),
            formatter: (cell) => {
                if (!cell) return null;
                return (
                    <div className="flex flex-wrap gap-1">
                        {cell.slice(0, 2).map((student, index) => (
                            <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                {student.name}
                            </span>
                        ))}
                        {cell.length > 2 && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                +{cell.length - 2}
                            </span>
                        )}
                    </div>
                );
            }
        },
        {
            key: 'status',
            label: t('class_management.class_status'),
            formatter: (cell) => {
                if (!cell) return null;
                return (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${String(cell).toUpperCase() === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                        {t(`class_management.${String(cell).toLowerCase()}`, { defaultValue: cell })}
                    </span>
                );
            }
        },
        {
            key: 'actions',
            label: t('actions'),
            formatter: (_, row) => {
                return (
                    <div className="flex items-center gap-3">
                        <Link
                            to={`/admin/classdetail/${row.classId || row.id}`}
                            className="text-green-600 hover:text-green-800 transition-colors duration-200"
                            title={t('view')}
                        >
                            <FaEye size={18} />
                        </Link>
                        <button
                            className="text-blue-600 hover:text-blue-800 transition-colors duration-200"
                            title={t('edit')}
                        >
                            <FaEdit size={18} />
                        </button>
                        <button
                            className="text-red-600 hover:text-red-800 transition-colors duration-200"
                            title={t('remove')}
                        >
                            <FaTrash size={18} />
                        </button>
                    </div>
                );
            }
        }
    ];

    const fetchClasses = useCallback(async () => {
        try {
            setLoading(true);
            const response = await ApiPost(`/contents/classes/search?pageSize=${limit}`, {
                country: 'LT',
            });
            const list = parseListResponse(response).filter(
                (item) => item.status !== 'DELETED' && item.classId
            );
            setClasses(list);
        } catch (error) {
            console.error('Error fetching classes:', error);
        } finally {
            setLoading(false);
        }
    }, [limit, t]);

    useEffect(() => {
        fetchClasses();
    }, [fetchClasses]);

    const handlePageChange = (selectedItem) => {
        setCurrentPage(selectedItem.selected);
    };

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleStatusFilter = (selectedOption) => {
        setFilterStatus(selectedOption.value);
    };

    const filteredClasses = classes.filter(classItem => {
        const matchesSearch =
            (classItem.className || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (classItem.description || '').toLowerCase().includes(searchQuery.toLowerCase());
        const matchesStatus = filterStatus === 'all' || String(classItem.status || '').toLowerCase() === filterStatus;
        return matchesSearch && matchesStatus;
    });

    const statusOptions = [
        { value: 'all', label: t('all') },
        { value: 'active', label: t('class_management.active') },
        { value: 'inactive', label: t('class_management.inactive') }
    ];

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="bg-white rounded-lg shadow p-6">
                {/* Header */}
                <div className="flex flex-wrap justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">{t('class_management.title')}</h1>
                    <div className="flex gap-4 md:mt-4 sm:flex-col">
                        {/* <button className="primary-btn">
                            <BiImport size={20} />
                            {t('class_management.import_csv')}
                        </button> */}
                        <button
                            type="button"
                            onClick={() => setIsAddOpen(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                        >
                            <FaPlus size={20} />
                            {t('class_management.create_class')}
                        </button>
                    </div>
                </div>

                {/* Search and Filters */}
                <div className="flex flex-wrap gap-4 mb-6">
                    <div className="flex-1 min-w-[200px]">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder={t('class_management.search_placeholder')}
                                className="w-1/2 md:w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={searchQuery}
                                onChange={handleSearch}
                            />
                            <IoMdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        </div>
                    </div>
                    <div className="min-w-[200px]">
                        <Select
                            options={statusOptions}
                            placeholder={t('class_management.filter_by_status')}
                            onChange={handleStatusFilter}
                            defaultValue={statusOptions[0]}
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </div>
                </div>

                {/* Data Table */}
                <>
                    <TailwindDataTable
                        columns={columns}
                        data={filteredClasses}
                        thClasses='text-white'
                        loading={loading}
                    />

                    {/* Pagination Controls */}
                    <div className="flex justify-between items-center mt-4">
                        <div className='xs:mb-5'>
                            <PageSizeSelectBox selected={limit} setSelected={setLimit} setPage={setCurrentPage} />
                        </div>
                        <div className='flex gap-4'>
                            <ReactPaginate
                                breakLabel="..."
                                nextLabel=">"
                                onPageChange={handlePageChange}
                                pageRangeDisplayed={5}
                                pageCount={Math.ceil(filteredClasses.length / limit)}
                                previousLabel="<"
                                renderOnZeroPageCount={null}
                                containerClassName="flex gap-2"
                                pageClassName="px-3 py-1 border rounded hover:bg-gray-100"
                                activeClassName="bg-blue-500 text-white"
                                previousClassName="px-3 py-1 border rounded hover:bg-gray-100"
                                nextClassName="px-3 py-1 border rounded hover:bg-gray-100"
                                disabledClassName="opacity-50 cursor-not-allowed"
                            />
                        </div>
                    </div>
                </>
                <CreateClassmodel
                    open={isAddOpen}
                    setOpen={setIsAddOpen}
                    getClasses={fetchClasses}
                />
            </div>
        </div>
    );
}
